import { AmazonTrackingInput } from 'src/gql/generated';

export interface ParsedTrackingLine {
  lineNo: number;
  input: AmazonTrackingInput;
}

export interface ParseError {
  lineNo: number;
  raw: string;
  reason: string;
}

export interface ParseResult {
  valid: ParsedTrackingLine[];
  errors: ParseError[];
}

const ASIN_REGEX = /^[A-Z0-9]{10}$/;

/**
 * TSV/CSV 形式の貼付テキストを AmazonTrackingInput 配列にパースする。
 *
 * 仕様:
 * - 行区切りは \n (CRLF は \r も trim で吸収)
 * - 空行と `#` 始まりのコメント行は無視
 * - デリミタは最初の非空・非コメント行で自動判定 (タブ優先、なければカンマ)
 * - 3列固定: [ASIN, memo, probability]
 * - ASIN は /^[A-Z0-9]{10}$/
 * - memo 空文字は null
 * - probability 空文字は null、数値は 0-100 の整数のみ
 * - 入力内で同じ ASIN が複数回出現した場合、最初の出現のみ採用し、以降は行エラー
 */
export function parseTsvCsv(input: string): ParseResult {
  const result: ParseResult = { valid: [], errors: [] };

  if (!input) {
    return result;
  }

  const lines = input.split('\n');

  // デリミタ判定: 最初の非空・非コメント行をチェック
  let delimiter = '\t';
  for (const line of lines) {
    const trimmed = line.trim();
    if (trimmed && !trimmed.startsWith('#')) {
      delimiter = trimmed.includes('\t') ? '\t' : ',';
      break;
    }
  }

  const seenAsins = new Set<string>();

  lines.forEach((raw, idx) => {
    const lineNo = idx + 1;
    const trimmed = raw.trim();
    if (!trimmed || trimmed.startsWith('#')) {
      return;
    }

    const cols = trimmed.split(delimiter).map((c) => c.trim());
    const asinRaw = cols[0];
    const memoRaw = cols[1];
    const probRaw = cols[2];

    if (!asinRaw || !ASIN_REGEX.test(asinRaw)) {
      result.errors.push({
        lineNo,
        raw,
        reason: 'ASIN形式が不正 (10文字の英数字)',
      });
      return;
    }

    if (seenAsins.has(asinRaw)) {
      result.errors.push({
        lineNo,
        raw,
        reason: '同一入力内で ASIN 重複',
      });
      return;
    }

    const memo: string | null = memoRaw ? memoRaw : null;

    let probability: number | null = null;
    if (probRaw !== undefined && probRaw !== '') {
      const n = Number(probRaw);
      if (!Number.isInteger(n) || n < 0 || n > 100) {
        result.errors.push({
          lineNo,
          raw,
          reason: '確率は 0-100 の整数',
        });
        return;
      }
      probability = n;
    }

    seenAsins.add(asinRaw);
    result.valid.push({
      lineNo,
      input: {
        asin: asinRaw,
        memo,
        probability,
      },
    });
  });

  return result;
}
